import React from 'react';
import PropTypes from 'prop-types';
import { reportClientError } from '../../utils/errorReporter';
import {
  Actions,
  ErrorBox,
  ErrorTitle,
  ErrorText,
  PrimaryButton,
  SecondaryButton,
} from './styles';

const resetKeysChanged = (previousKeys = [], nextKeys = []) => (
  previousKeys.length !== nextKeys.length
  || previousKeys.some((key, index) => !Object.is(key, nextKeys[index]))
);

function DefaultFallback({ variant, onRetry }) {
  const isGlobal = variant === 'global';

  return (
    <ErrorBox role="alert" $variant={variant}>
      <ErrorTitle>
        {isGlobal
          ? 'O chat encontrou um erro inesperado.'
          : 'Não foi possível carregar esta área.'}
      </ErrorTitle>
      <ErrorText>
        {isGlobal
          ? 'Recarregue a aplicação ou tente novamente.'
          : 'Você pode tentar carregar novamente sem sair da conversa.'}
      </ErrorText>
      <Actions>
        <PrimaryButton type="button" onClick={onRetry}>
          Tentar novamente
        </PrimaryButton>
        <SecondaryButton type="button" onClick={() => window.location.reload()}>
          Recarregar
        </SecondaryButton>
      </Actions>
    </ErrorBox>
  );
}

DefaultFallback.propTypes = {
  onRetry: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['global', 'section']).isRequired,
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
      retryKey: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(previousProps) {
    const { resetKeys } = this.props;
    const { error } = this.state;

    if (error && resetKeysChanged(previousProps.resetKeys, resetKeys)) {
      this.clearError();
    }
  }

  componentDidCatch(error, info) {
    const { area, onError } = this.props;

    onError(error, {
      area,
      componentStack: info.componentStack,
      severity: 'error',
      source: 'react-error-boundary',
    });
  }

  clearError = () => {
    this.setState((state) => ({
      error: null,
      retryKey: state.retryKey + 1,
    }));
  };

  render() {
    const {
      children,
      fallback: Fallback,
      variant,
    } = this.props;
    const { error, retryKey } = this.state;

    if (error) {
      return <Fallback error={error} onRetry={this.clearError} variant={variant} />;
    }

    return <React.Fragment key={retryKey}>{children}</React.Fragment>;
  }
}

ErrorBoundary.propTypes = {
  area: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  fallback: PropTypes.elementType,
  onError: PropTypes.func,
  resetKeys: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ])),
  variant: PropTypes.oneOf(['global', 'section']),
};

ErrorBoundary.defaultProps = {
  fallback: DefaultFallback,
  onError: reportClientError,
  resetKeys: [],
  variant: 'section',
};

export default ErrorBoundary;
